import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { useClinicConfig } from "@/lib/clinicConfig";
import VisitActions from "@/components/app/VisitActions";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Send, ChevronLeft, ChevronRight, Info, Clock, User, Sparkles, Loader2, X, ArrowRight, Users, Settings as SettingsIcon, RefreshCw, CheckCircle2, XCircle, AlertCircle, UserCheck, Stethoscope } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Time24Input } from "@/components/Time24Input";
import { DateDMYInput } from "@/components/DateDMYInput";

// ---- helpers
const DEFAULT_START_HOUR = 9;   // fallback if clinic has no working_hours
const DEFAULT_END_HOUR = 18;
const SLOT_MIN = 30;             // 30-min rows
const PX_PER_MIN = 1.2;          // column height scale

const DAY_NAMES = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

// Curated palette used to auto-assign a distinct colour to each doctor.
// Order chosen so the first N doctors get high-contrast, brand-aligned hues.
const DOCTOR_PALETTE = [
  { key: "pine",   bg: "#E7EEE9", fg: "#1A362B", tint: "#8FB19E" },
  { key: "clay",   bg: "#FDF3ED", fg: "#8A3F1E", tint: "#D06A42" },
  { key: "plum",   bg: "#F0E7EE", fg: "#4A2148", tint: "#8C5290" },
  { key: "sand",   bg: "#F5EFE0", fg: "#7A5A20", tint: "#C8A75C" },
  { key: "sky",    bg: "#E4EFF5", fg: "#1F4B66", tint: "#5A93B5" },
  { key: "forest", bg: "#E0EBDE", fg: "#264029", tint: "#5B8F4F" },
  { key: "rose",   bg: "#F6E3E5", fg: "#7A2A2E", tint: "#C2545A" },
  { key: "coal",   bg: "#E4E7E6", fg: "#212B27", tint: "#5D6863" },
];

function doctorColorFor(doctorId) {
  if (!doctorId) return { bg: "#EDEEEE", fg: "#5B6660", tint: "#98A19C", key: "muted" };
  // Stable hash → palette index
  let h = 0;
  for (let i = 0; i < doctorId.length; i++) h = (h * 31 + doctorId.charCodeAt(i)) >>> 0;
  return DOCTOR_PALETTE[h % DOCTOR_PALETTE.length];
}

const fmt = (d) => d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
const isoDate = (d) => {
  // Local Y-M-D (avoids UTC shift that hides appointments in IST/other tz)
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
const clone = (d) => new Date(d.getTime());

function parseHM(hm, fallback) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hm || "");
  if (!m) return fallback;
  return { h: Number(m[1]), min: Number(m[2]) };
}

import { dayShifts as _wh_dayShifts,
         inShifts as _wh_inShifts,
         apptFitsAnyShift as _wh_apptFits,
         hasOutOfHoursAppointment as _wh_hasOOH,
         isAppointmentOutOfHours as _wh_isOOH,
         shiftsForDay as _wh_shiftsForDay } from "@/lib/workingHours";
// Re-export under the local names the file has used since 2025 so no
// downstream reference has to change. The ONE source of truth is now
// `/app/frontend/src/lib/workingHours.js` and is shared with the tests
// under `/app/frontend/src/lib/__tests__/workingHours.test.mjs`.
const dayShifts = _wh_dayShifts;
const inShifts  = _wh_inShifts;

function dayWindow(day, workingHours) {
  const shifts = dayShifts(workingHours?.[DAY_NAMES[day.getDay()]]);
  if (!shifts.length) return null;
  return {
    open: shifts[0].open,
    close: shifts[shifts.length - 1].close,
    shifts,
  };
}



function buildDaySlots(day, workingHours, apptsToday = []) {
  const w = dayWindow(day, workingHours);
  const openHM = w?.open || { h: DEFAULT_START_HOUR, min: 0 };
  const closeHM = w?.close || { h: DEFAULT_END_HOUR, min: 0 };
  const shifts = w?.shifts || [];
  // Working-hours envelope (first shift open → last shift close)
  let startMs = new Date(day).setHours(openHM.h, openHM.min, 0, 0);
  let endMs = new Date(day).setHours(closeHM.h, closeHM.min, 0, 0);
  // Extend to fit any out-of-hours appointments (start OR end)
  for (const a of apptsToday) {
    const aStart = new Date(a.start).getTime();
    const aEnd = aStart + (a.duration_min || 30) * 60000;
    if (aStart < startMs) startMs = aStart;
    if (aEnd > endMs) endMs = aEnd;
  }
  // Snap start down / end up to a full slot boundary
  const slotMs = SLOT_MIN * 60000;
  startMs = Math.floor(startMs / slotMs) * slotMs;
  endMs = Math.ceil(endMs / slotMs) * slotMs;
  const out = [];
  for (let t = startMs; t < endMs; t += slotMs) {
    const d = new Date(t);
    // A slot is "in-hours" only if it lies inside one of the configured shifts.
    // Slots between shifts (breaks) get flagged as outOfHours → visually striped
    // and non-interactive.
    const inHours = shifts.length ? inShifts(shifts, d) : true;
    out.push({ time: d, outOfHours: !inHours });
  }
  return out;
}

function statusColor(s) {
  return s === "completed" ? { bg: "#E8F0EB", fg: "#2D6A4F", border: "#B7D4C4" }
       : s === "no_show"   ? { bg: "#FCECEC", fg: "#B94A48", border: "#EBBDBB" }
       : s === "cancelled" ? { bg: "#EDEDED", fg: "#6B7280", border: "#D1D5DB" }
       : { bg: "#FDF3ED", fg: "#8A3F1E", border: "#EAC5AE" };
}

export default function CalendarPage() {
  const { user } = useAuth();
  // Plan / caps / feature flags — the ONLY permitted source. Never read
  // `clinic.plan` directly here or the Calendar will fall out of sync with
  // Platform-Admin plan changes.
  const cfg = useClinicConfig();
  const nav = useNavigate();
  const isDoctor = user?.role === "doctor";
  const [day, setDay] = useState(() => { const d = new Date(); d.setHours(0,0,0,0); return d; });
  const [chairs, setChairs] = useState([]);
  const [appts, setAppts] = useState([]);
  const [patients, setPatients] = useState([]);
  const [team, setTeam] = useState([]);          // { id, name, role, picture, ... }[]
  const [util, setUtil] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // appointment being edited (null = create mode)
  const [editingQueue, setEditingQueue] = useState(null); // matching queue row (if any)
  const [prefill, setPrefill] = useState(null);
  // Doctors default to seeing only their own appointments — matches real
  // clinic workflow. They can flip to "All" to help cover a colleague.
  const [mineOnly, setMineOnly] = useState(isDoctor);
  const [form, setForm] = useState({ patient_id: "", chair_id: "", start: "", duration_min: 30, procedure: "Consultation", status: "scheduled", notes: "" });

  // Chair ownership modal (admin only)
  const [chairMgrOpen, setChairMgrOpen] = useState(false);
  const [scheduleOverview, setScheduleOverview] = useState([]);
  const [scheduleDoctorId, setScheduleDoctorId] = useState(null); // opens the schedule editor

  // ---- AI Auto-optimize day ----
  const [optOpen, setOptOpen] = useState(false);
  const [optPlan, setOptPlan] = useState(null);
  const [optLoading, setOptLoading] = useState(false);
  const [optApplying, setOptApplying] = useState(false);
  const canManageCalendar = user?.role === "admin" || user?.role === "receptionist";

  // Google-imported "busy" events for the day — rendered as read-only
  // blocked cards on the calendar so the doctor can see external commitments
  // without opening Google Calendar. Populated by /google-calendar/busy-events
  // and refreshed on every load() + on `viyora:gcal-synced` broadcasts.
  const [gcalBusy, setGcalBusy] = useState([]);

  const load = useCallback(async () => {
    const [c, a, p, u, cl, t, s, g] = await Promise.all([
      api.get("/chairs"),
      api.get("/appointments", { params: (isDoctor && mineOnly) ? { mine: true } : {} }),
      api.get("/patients"),
      api.get("/chairs/utilization", { params: { date: isoDate(day) } }),
      api.get("/clinics/me"),
      api.get("/team").catch(() => ({ data: [] })),
      api.get("/team/schedule-overview", { params: { date_str: isoDate(day) } }).catch(() => ({ data: [] })),
      // Pull the Google-Calendar blocked slots for the ±3-day window
      // around the day being viewed. Errors here are non-fatal (the
      // rest of the calendar must still load if Google is down or the
      // doctor never connected).
      api.get("/google-calendar/busy-events", {
        params: {
          date_from: isoDate(new Date(day.getTime() - 3 * 24 * 3600 * 1000)),
          date_to:   isoDate(new Date(day.getTime() + 3 * 24 * 3600 * 1000)),
          ...(isDoctor && mineOnly ? { doctor_id: user?.id } : {}),
        },
      }).catch(() => ({ data: { events: [] } })),
    ]);
    setChairs(c.data); setAppts(a.data); setPatients(p.data); setUtil(u.data); setClinic(cl.data);
    setTeam(t.data || []);
    setScheduleOverview(s.data || []);
    setGcalBusy(g.data?.events || []);
    // Force the centralized config to refresh too — keeps caps + counts +
    // features aligned with the latest chair / doctor mutations without
    // waiting for the 15s poll.
    cfg.refresh?.();
  }, [day, mineOnly, isDoctor, user?.id, cfg]);
  useEffect(() => { load(); }, [load]);

  // Auto-refetch whenever a Google-Calendar sync completes anywhere in the
  // app. CalendarIntegration.syncNow() and the OAuth popup callback both
  // dispatch `viyora:gcal-synced`; listening here means the freshly-imported
  // Google events show up as blocked slots *without any page reload*.
  useEffect(() => {
    const onGcalSynced = () => { load(); };
    window.addEventListener("viyora:gcal-synced", onGcalSynced);
    return () => window.removeEventListener("viyora:gcal-synced", onGcalSynced);
  }, [load]);

  // Refetch just the Google busy-events layer.  Cheap (single endpoint) so
  // we can call it on window focus / tab-visible without re-pulling chairs,
  // appointments, and clinic config every time.  Users notice reschedules
  // in Google within a few seconds of tabbing back to Viyora — no full reload,
  // no 5-minute wait for the scheduler.
  const refreshGcalBusy = useCallback(async () => {
    try {
      const { data } = await api.get("/google-calendar/busy-events", {
        params: {
          date_from: isoDate(new Date(day.getTime() - 3 * 24 * 3600 * 1000)),
          date_to:   isoDate(new Date(day.getTime() + 3 * 24 * 3600 * 1000)),
          ...(isDoctor && mineOnly ? { doctor_id: user?.id } : {}),
        },
      });
      setGcalBusy(data?.events || []);
    } catch { /* non-fatal — keep previous state */ }
  }, [day, mineOnly, isDoctor, user?.id]);

  // Force an immediate Google → Viyora pull, then refetch the overlay. Used
  // by the toolbar "Refresh" button; also invoked implicitly on window focus
  // so users don't need to click when returning from Google Calendar.
  const [syncing, setSyncing] = useState(false);
  const syncGcalNow = useCallback(async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/google-calendar/sync-now");
      await refreshGcalBusy();
      const brought = (data?.updated || 0) + (data?.inserted || 0);
      toast.success(
        brought > 0
          ? `Synced Google Calendar — ${data.inserted || 0} new, ${data.updated || 0} rescheduled`
          : "Google Calendar is up to date"
      );
    } catch (e) {
      toast.error(apiError(e, "Google sync failed"));
    } finally {
      setSyncing(false);
    }
  }, [refreshGcalBusy]);

  // Refetch on window focus / tab visible.  Cheap read; keeps the blocked-
  // slot overlay in sync with reschedules made in another tab or in the
  // native Google Calendar UI.
  useEffect(() => {
    const onFocus = () => refreshGcalBusy();
    const onVisible = () => { if (document.visibilityState === "visible") onFocus(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refreshGcalBusy]);

  // Derived: quick lookup + solo/multi-doctor detection + plan gate
  const doctors = useMemo(() => team.filter((u) => u.role === "doctor" || u.role === "admin"), [team]);
  const doctorMap = useMemo(() => Object.fromEntries(doctors.map((d) => [d.id, d])), [doctors]);
  const scheduleMap = useMemo(() => Object.fromEntries((scheduleOverview || []).map((s) => [s.id, s])), [scheduleOverview]);
  const activeDoctorCount = doctors.length;
  // Grid layout choice — cosmetic. Uses live headcount, NOT plan tier.
  const isSolo = activeDoctorCount <= 1 && chairs.length <= 1;
  // Plan gate: soft banner shown when the current clinic is on Solo tier but
  // has grown beyond its cap. `cfg.tier` is `"solo" | "clinic"` and updates
  // instantly via ClinicConfigProvider — no local plan reads.
  const chairCap  = cfg.caps?.chairs ?? 1;
  const doctorCap = cfg.caps?.doctors ?? 1;
  const needsUpgrade =
    cfg.tier === "solo" && (activeDoctorCount > doctorCap || chairs.length > chairCap);

  // Working hours: prefer the centralized config (updates instantly across
  // tabs) and fall back to the local /clinics/me hydration if the provider
  // hasn't resolved yet.
  const workingHours = cfg.workingHours || clinic?.working_hours;
  const isClinicClosed = useMemo(() => !dayWindow(day, workingHours), [day, workingHours]);

  const apptsToday = useMemo(() => {
    const dayStr = isoDate(day);
    // Compare LOCAL date of appointment start (not the raw UTC-sliced ISO string)
    return appts.filter((a) => isoDate(new Date(a.start)) === dayStr);
  }, [appts, day]);

  const slots = useMemo(() => buildDaySlots(day, workingHours, apptsToday), [day, workingHours, apptsToday]);
  // BANNER fires only when at least ONE real appointment fails the
  // full-INTERVAL fit check. Previously we did `slots.some(s.outOfHours)`
  // which trivially became true for any clinic with a shift break — even
  // when zero appointments actually overlapped the break. See
  // /app/frontend/src/lib/__tests__/workingHours.test.mjs for the locked
  // rules (16:00 start on 09-13/16-20 split shifts MUST stay silent).
  const todayShiftsForBanner = useMemo(
    () => _wh_shiftsForDay(workingHours, day),
    [workingHours, day]);
  const hasOutOfHours = useMemo(
    () => _wh_hasOOH(todayShiftsForBanner, apptsToday),
    [todayShiftsForBanner, apptsToday]);

  const openCreate = (chairId, slot) => {
    const slotDate = slot?.time || slot;  // support both {time,...} and raw Date
    // Block creates on gap/closed slots. `slot` may be a raw Date (from "New
    // appointment" quick button), so we re-check against the shift config.
    const dayShiftList = dayShifts(workingHours?.[DAY_NAMES[slotDate.getDay()]]);
    const outOfHours = slot?.outOfHours === true
      || (dayShiftList.length ? !inShifts(dayShiftList, slotDate) : false);
    if (outOfHours) {
      toast.error("This slot falls outside the clinic's working hours. Update Settings → Working hours or pick another slot.");
      return;
    }
    const iso = slotDate ? new Date(slotDate.getTime() - slotDate.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : "";
    setEditing(null);
    setPrefill({ chair_id: chairId, start: iso });
    setForm((f) => ({ ...f, chair_id: chairId || "", start: iso, duration_min: 30, procedure: "Consultation", status: "scheduled", patient_id: "", notes: "" }));
    setOpen(true);
  };

  const openEdit = async (appt) => {
    // Format ISO -> datetime-local (YYYY-MM-DDTHH:MM in local tz)
    const d = new Date(appt.start);
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setEditing(appt);
    setEditingQueue(null);
    setForm({
      patient_id: appt.patient_id || "",
      chair_id: appt.chair_id || "",
      start: local,
      duration_min: appt.duration_min || 30,
      procedure: appt.procedure || "Consultation",
      status: appt.status || "scheduled",
      notes: appt.notes || "",
    });
    setOpen(true);
    // Fetch matching queue entry (if any) in the background so VisitActions
    // renders the correct button set even after the patient has checked in.
    api.get("/checkin/queue").then(({ data }) => {
      const match = (data?.queue || []).find((q) => q.appointment_id === appt.id);
      setEditingQueue(match || null);
    }).catch(() => setEditingQueue(null));
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        start: new Date(form.start).toISOString(),
        duration_min: Number(form.duration_min),
        ...(isDoctor && !form.doctor_id ? { doctor_id: user.id } : {}),
      };
      if (editing) {
        await api.put(`/appointments/${editing.id}`, payload);
        toast.success("Appointment updated");
      } else {
        await api.post("/appointments", payload);
        toast.success("Appointment created");
      }
      setOpen(false); setEditing(null); load();
    } catch (err) {
      toast.error(apiError(err, editing ? "Failed to update" : "Failed to create"));
    }
  };

  const removeAppt = async () => {
    if (!editing) return;
    if (!window.confirm("Delete this appointment? This cannot be undone.")) return;
    try {
      await api.delete(`/appointments/${editing.id}`);
      toast.success("Appointment deleted");
      setOpen(false); setEditing(null); load();
    } catch (err) {
      toast.error(apiError(err, "Failed to delete"));
    }
  };

  const move = async (apptId, chairId, newStart) => {
    try {
      await api.patch(`/appointments/${apptId}/move`, {
        chair_id: chairId,
        start: newStart.toISOString(),
      });
      toast.success("Rescheduled");
      load();
    } catch (err) {
      toast.error(apiError(err, "Move failed"));
    }
  };

  const sendReminder = async (a) => {
    const p = patients.find((x) => x.id === a.patient_id);
    const { data } = await api.post("/reminders/send", {
      channel: "whatsapp", to: p?.phone,
      message: `Hi ${p?.name || "there"}, reminder of your ${a.procedure} on ${new Date(a.start).toLocaleString()}.`,
      appointment_id: a.id,
    });
    if (data.mocked) {
      toast.success(`WhatsApp queued (mocked — configure WhatsApp Business API keys)`);
    } else {
      toast.success(`WhatsApp sent via ${data.provider || "provider"}`);
    }
  };

  const shiftDay = (n) => { const d = clone(day); d.setDate(d.getDate() + n); setDay(d); };

  const requestOptimize = async () => {
    setOptOpen(true);
    setOptPlan(null);
    setOptLoading(true);
    try {
      const { data } = await api.post("/calendar/optimize-day", { date: isoDate(day) });
      setOptPlan(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not optimise this day");
      setOptOpen(false);
    } finally { setOptLoading(false); }
  };

  const applyOptimize = async () => {
    if (!optPlan?.moves?.length) return;
    setOptApplying(true);
    try {
      const payload = {
        moves: optPlan.moves.map((m) => ({
          appt_id: m.appt_id,
          from_chair_id: m.from.chair_id,
          from_start: m.from.start,
          to_chair_id: m.to.chair_id,
          to_start: m.to.start,
        })),
      };
      const { data } = await api.post("/calendar/optimize-day/apply", payload);
      toast.success(
        data.skipped
          ? `Applied ${data.applied} of ${data.applied + data.skipped} moves (${data.skipped} skipped)`
          : `Applied ${data.applied} move${data.applied === 1 ? "" : "s"} · day optimised`
      );
      setOptOpen(false); setOptPlan(null);
      load();
    } catch (err) {
      toast.error(apiError(err, "Apply failed"));
    } finally { setOptApplying(false); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <span className="overline text-ink-soft">Smart calendar</span>
          <h1 className="mt-2 font-display text-4xl tracking-tight">Chair-by-chair, drag to reschedule.</h1>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="cal-prev-day" onClick={() => shiftDay(-1)} className="w-10 h-10 rounded-full border border-[#E8EAE9] grid place-items-center hover:bg-white"><ChevronLeft className="w-4 h-4" /></button>
          <div className="px-4 py-2 rounded-full bg-white border border-[#E8EAE9] font-display text-sm min-w-[220px] text-center" data-testid="cal-day-label">
            {day.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
          </div>
          <button data-testid="cal-next-day" onClick={() => shiftDay(1)} className="w-10 h-10 rounded-full border border-[#E8EAE9] grid place-items-center hover:bg-white"><ChevronRight className="w-4 h-4" /></button>
          <button data-testid="cal-today" onClick={() => { const d = new Date(); d.setHours(0,0,0,0); setDay(d); }} className="ml-2 text-xs text-ink-mid hover:text-ink">Today</button>
          {isDoctor && (
            <div className="ml-3 inline-flex rounded-full bg-white border border-[#E8EAE9] p-1 text-xs" data-testid="doctor-scope-toggle">
              <button
                onClick={() => setMineOnly(true)}
                data-testid="scope-mine"
                className={`px-3 py-1 rounded-full inline-flex items-center gap-1 ${mineOnly ? "bg-pine text-linen" : "text-ink-mid hover:text-ink"}`}
                title="Show only your appointments"
              >
                <User className="w-3.5 h-3.5" /> Mine
              </button>
              <button
                onClick={() => setMineOnly(false)}
                data-testid="scope-all"
                className={`px-3 py-1 rounded-full ${!mineOnly ? "bg-pine text-linen" : "text-ink-mid hover:text-ink"}`}
                title="Show the whole clinic"
              >
                All clinic
              </button>
            </div>
          )}
          {user?.role === "admin" && (
            <button
              data-testid="btn-manage-chairs"
              onClick={() => setChairMgrOpen(true)}
              title="Assign chairs to doctors"
              className="w-10 h-10 rounded-full border border-[#E8EAE9] grid place-items-center hover:bg-white ml-1"
            >
              <SettingsIcon className="w-4 h-4" />
            </button>
          )}
          {/* Google-Calendar refresh — forces an immediate Google → Viyora
              pull so drag/reschedule in Google shows up here in ~1s instead
              of waiting for the 5-minute scheduler tick. */}
          <button
            data-testid="btn-gcal-refresh"
            onClick={syncGcalNow}
            disabled={syncing}
            title="Refresh Google Calendar now"
            className="w-10 h-10 rounded-full border border-[#E8EAE9] grid place-items-center hover:bg-white ml-1 disabled:opacity-50 disabled:cursor-wait"
          >
            {syncing
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <RefreshCw className="w-4 h-4" />}
          </button>
          {isDoctor && (
            <button
              data-testid="btn-my-schedule"
              onClick={() => setScheduleDoctorId(user?.id)}
              title="My working hours and leaves"
              className="ml-1 text-xs inline-flex items-center gap-1.5 rounded-full border border-[#E8EAE9] bg-white px-3 py-2 hover:bg-linen"
            >
              <Clock className="w-3.5 h-3.5" /> My schedule
            </button>
          )}
          {canManageCalendar && (
            <button
              data-testid="btn-ai-optimize-day"
              onClick={requestOptimize}
              disabled={optLoading || isClinicClosed}
              title="Let Viyora AI reshuffle today's schedule to minimise idle chair time"
              className="ml-2 text-sm inline-flex items-center gap-2 rounded-full bg-gradient-to-br from-clay to-[#B54C22] text-linen px-4 py-2 hover:brightness-110 active:scale-[0.98] transition disabled:opacity-40 disabled:cursor-not-allowed">
              {optLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              AI optimise day
            </button>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="btn-new-appointment" onClick={() => openCreate(chairs[0]?.id, slots.find((s) => !s.outOfHours) || slots[0])} className="btn-primary text-sm inline-flex items-center gap-2 ml-4"><Plus className="w-4 h-4" /> New appointment</button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader><DialogTitle className="font-display text-2xl">New appointment</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-3" data-testid="new-appointment-form">
                <select required data-testid="appt-patient" value={form.patient_id} onChange={(e) => setForm({ ...form, patient_id: e.target.value })} className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm bg-white">
                  <option value="">Select patient</option>
                  {patients.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <div className="grid grid-cols-2 gap-3">
                  <select data-testid="appt-chair" value={form.chair_id} onChange={(e) => setForm({ ...form, chair_id: e.target.value })} className="rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm bg-white">
                    <option value="">No chair</option>
                    {chairs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <div className="flex flex-col">
                    <label htmlFor="appt-duration" className="text-[11px] uppercase tracking-widest text-ink-soft mb-1">Duration (minutes)</label>
                    <input id="appt-duration" data-testid="appt-duration" type="number" min={15} step={15} value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} className="rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm" />
                  </div>
                </div>
                <input required data-testid="appt-start" type="datetime-local" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm" />
                <select data-testid="appt-procedure" value={form.procedure} onChange={(e) => setForm({ ...form, procedure: e.target.value })} className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm bg-white">
                  {["Consultation","Routine Cleaning","Cavity Filling","Root Canal","Crown Fitting","Whitening"].map((x) => <option key={x}>{x}</option>)}
                </select>
                {editing && (
                  <select data-testid="appt-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm bg-white">
                    {["scheduled","checked_in","in_chair","completed","cancelled","no_show"].map((s) => <option key={s} value={s}>{s.replace("_"," ").replace(/\b\w/g, c=>c.toUpperCase())}</option>)}
                  </select>
                )}
                <textarea data-testid="appt-notes" placeholder="Notes (optional)" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm resize-none" />
                {editing && (
                  <VisitActions
                    appointment={editing}
                    queueEntry={editingQueue}
                    onStartVisit={async () => {
                      try {
                        const { data } = await api.post("/checkin/start-visit", { appointment_id: editing.id });
                        toast.success("Visit started — patient is in chair");
                        setEditingQueue(data.queue_entry || null);
                        setOpen(false); setEditing(null);
                        load();
                      } catch (e) { toast.error(apiError(e, "Could not start visit")); }
                    }}
                    onCheckIn={async () => {
                      try {
                        const { data } = await api.post("/checkin", { appointment_id: editing.id });
                        toast.success("Patient checked in — visible in Waiting Room");
                        setEditingQueue(data.queue_entry || null);
                        setOpen(false); setEditing(null);
                        setForm((f) => ({ ...f, status: "checked_in" }));
                        load();
                      } catch (e) { toast.error(apiError(e, "Check-in failed")); }
                    }}
                    onMarkInChair={async () => {
                      try {
                        if (!editingQueue?.id) {
                          // No queue row yet — cascade via start-visit.
                          await api.post("/checkin/start-visit", { appointment_id: editing.id });
                        } else {
                          await api.patch(`/checkin/${editingQueue.id}/status`, { status: "in_chair" });
                        }
                        toast.success("Patient marked in chair");
                        setOpen(false); setEditing(null); load();
                      } catch (e) { toast.error(apiError(e, "Could not mark in chair")); }
                    }}
                    onComplete={async () => {
                      try {
                        if (editingQueue?.id) {
                          await api.patch(`/checkin/${editingQueue.id}/status`, { status: "done" });
                        } else {
                          await api.put(`/appointments/${editing.id}`, { ...editing, status: "completed" });
                        }
                        toast.success("Visit clinical work complete");
                        setOpen(false); setEditing(null); load();
                      } catch (e) { toast.error(apiError(e, "Could not complete visit")); }
                    }}
                    onOpenChairside={() => {
                      setOpen(false); setEditing(null);
                      nav(`/app/patients/${editing.patient_id}`);
                    }}
                    onOpenBilling={() => {
                      setOpen(false); setEditing(null);
                      nav(`/app/billing?patient_id=${editing.patient_id}`);
                    }}
                  />
                )}
                <div className="flex gap-2">
                  <button type="submit" data-testid="appt-submit" className="flex-1 btn-primary text-sm">
                    {editing ? "Save changes" : "Create appointment"}
                  </button>
                  {editing && (
                    <button type="button" onClick={removeAppt} data-testid="appt-delete" className="rounded-full border border-[#B94A48]/30 text-[#B94A48] hover:bg-[#B94A48]/5 px-4 py-2.5 text-sm">
                      Delete
                    </button>
                  )}
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Utilization strip */}
      <div className="card-widget mb-4" data-testid="utilization-panel">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline text-ink-soft">Chair utilization · {day.toLocaleDateString(undefined,{month:"short",day:"numeric"})}</div>
            <div className="mt-1 font-display text-3xl tracking-tight" data-testid="overall-utilization">{util?.overall_utilization ?? 0}%</div>
            <div className="text-xs text-ink-mid mt-1">Overall booked vs 9h/day capacity</div>
          </div>
          <div className="text-xs text-ink-mid max-w-xs flex items-start gap-2">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Drag any appointment card to another chair or time. Overlaps are blocked server-side.</span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {util?.chairs?.map((c) => (
            <div key={c.chair_id} className="rounded-xl border border-[#E8EAE9] p-3" data-testid={`util-${c.chair_id}`}>
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">{c.name}</span>
                <span className="text-ink-mid">{c.utilization}%</span>
              </div>
              <div className="mt-2 h-2 rounded-full bg-linen overflow-hidden">
                <div className="h-full bg-pine transition-all" style={{ width: `${Math.min(c.utilization, 100)}%` }} />
              </div>
              <div className="mt-1 text-xs text-ink-soft">{Math.round(c.booked_min / 60 * 10) / 10}h / 9h</div>
            </div>
          ))}
        </div>
      </div>

      {/* Chair grid */}
      {chairs.length === 0 ? (
        <div className="card-widget text-center text-ink-soft py-12">No chairs configured. Ask an admin to add operatories.</div>
      ) : isClinicClosed ? (
        <div className="card-widget text-center text-ink-mid py-16" data-testid="clinic-closed-banner">
          <div className="overline text-ink-soft mb-2">Clinic closed</div>
          <div className="font-display text-2xl text-ink">No hours scheduled for {day.toLocaleDateString(undefined, { weekday: "long" })}</div>
          <div className="mt-2 text-sm text-ink-soft">Update your weekly schedule in Settings → Working hours.</div>
        </div>
      ) : (
        <>
          {hasOutOfHours && (
            <div className="mb-3 rounded-2xl border border-[#EAC5AE] bg-[#FDF3ED] px-4 py-3 flex items-center gap-3" data-testid="out-of-hours-banner">
              <div className="w-8 h-8 rounded-lg bg-[#D06A42]/20 grid place-items-center flex-shrink-0">
                <Clock className="w-4 h-4 text-[#8A3F1E]" />
              </div>
              <div className="text-xs text-[#8A3F1E]">
                <span className="font-semibold">Appointments outside working hours detected.</span>{" "}
                Striped rows below fall in a shift break or outside the clinic&apos;s official schedule. New bookings there are blocked. Update hours in <a href="/app/settings" className="underline">Settings → Working hours</a>.
              </div>
            </div>
          )}
          <ChairGrid
            day={day} slots={slots} chairs={chairs} appts={apptsToday} patients={patients}
            doctorMap={doctorMap} scheduleMap={scheduleMap} isSolo={isSolo}
            workingHours={workingHours} gcalBusy={gcalBusy}
            onMove={move} onCellClick={openCreate} onSendReminder={sendReminder} onEdit={openEdit}
          />
        </>
      )}

      {/* Plan-gate soft banner — persistent CTA, never blocks reads */}
      {needsUpgrade && (
        <div className="mt-4 rounded-2xl border border-clay/40 bg-gradient-to-br from-clay/10 to-linen p-4 flex flex-wrap items-center gap-3 justify-between"
             data-testid="plan-upgrade-banner">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="w-9 h-9 rounded-full bg-clay/20 text-clay grid place-items-center shrink-0">
              <AlertCircle className="w-5 h-5" />
            </div>
            <div className="text-sm text-ink min-w-0">
              <div className="font-medium">Multiple doctors and chairs require the Clinic Plan.</div>
              <div className="text-ink-mid text-xs mt-0.5">
                You currently have <b>{activeDoctorCount} doctor{activeDoctorCount === 1 ? "" : "s"}</b> and
                {" "}<b>{chairs.length} chair{chairs.length === 1 ? "" : "s"}</b> configured.
                Upgrade to the Clinic Plan (₹1,499/month) to continue using multi-doctor scheduling without limits.
              </div>
            </div>
          </div>
          <a href="/app/subscription"
             data-testid="plan-upgrade-cta"
             className="text-sm inline-flex items-center gap-1.5 bg-pine text-linen px-4 py-2 rounded-full hover:bg-pine/90 shrink-0">
            <Sparkles className="w-4 h-4" /> Upgrade to Clinic
          </a>
        </div>
      )}

      <ChairOwnershipDialog
        open={chairMgrOpen}
        onClose={() => setChairMgrOpen(false)}
        chairs={chairs}
        doctors={doctors}
        scheduleMap={scheduleMap}
        onEditSchedule={(did) => setScheduleDoctorId(did)}
        onChanged={load}
      />

      <DoctorScheduleDialog
        doctorId={scheduleDoctorId}
        chairs={chairs}
        canEdit={user?.role === "admin" || (user?.role === "doctor" && user?.id === scheduleDoctorId)}
        onClose={() => setScheduleDoctorId(null)}
        onChanged={load}
      />

      <OptimizeDialog
        open={optOpen}
        onClose={() => { setOptOpen(false); setOptPlan(null); }}
        loading={optLoading}
        applying={optApplying}
        plan={optPlan}
        day={day}
        onApply={applyOptimize}
      />
    </div>
  );
}

// -----------------------------------------------------------------------------
// ChairGrid: columns = chairs, rows = 30-min slots. Absolutely-positioned appts.
// HTML5 drag-and-drop for reschedule.
// -----------------------------------------------------------------------------
function ChairGrid({ day, slots, chairs, appts, patients, doctorMap = {}, scheduleMap = {}, isSolo = false, workingHours, gcalBusy = [], onMove, onCellClick, onSendReminder, onEdit }) {
  const pname = (id) => patients.find((p) => p.id === id)?.name || "Patient";
  const colHeight = slots.length * SLOT_MIN * PX_PER_MIN;
  // Day start = first slot time (respects clinic working hours + any out-of-hours appts)
  const dayStart = slots[0] ? new Date(slots[0].time.getTime()) : new Date(day);
  // Shift list for the day — used to reject drops into gap regions early.
  const todayShifts = dayShifts(workingHours?.[DAY_NAMES[day.getDay()]]);

  const [dragging, setDragging] = useState(null);
  const gridRefs = useRef({});

  const onDragStart = (e, appt) => {
    setDragging(appt);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", appt.id);
  };

  const onDropOnChair = (e, chair) => {
    e.preventDefault();
    if (!dragging) return;
    const rect = gridRefs.current[chair.id].getBoundingClientRect();
    const y = e.clientY - rect.top;
    const minutesFromStart = Math.max(0, Math.round(y / PX_PER_MIN / SLOT_MIN) * SLOT_MIN);
    const newStart = new Date(dayStart.getTime() + minutesFromStart * 60000);
    // Client-side guard — mirrors the backend rule so the user sees the
    // rejection instantly (no round-trip flash).
    if (todayShifts.length && !inShifts(todayShifts, newStart)) {
      toast.error("Cannot drop into a break — pick a slot inside working shifts.");
      setDragging(null);
      return;
    }
    onMove(dragging.id, chair.id, newStart);
    setDragging(null);
  };

  const chairsToRender = isSolo ? chairs.slice(0, 1) : chairs;

  return (
    <div className="card-widget overflow-x-auto">
      <div className="flex" style={{ minWidth: 160 + chairsToRender.length * 240 }}>
        {/* Time column */}
        <div className="w-16 shrink-0">
          <div className="h-16 border-b border-[#E8EAE9]" />
          <div className="relative" style={{ height: colHeight }}>
            {slots.map((s, i) => (
              <div key={s.time}
                   className={`absolute left-0 right-2 text-[11px] font-mono -translate-y-1/2 ${s.outOfHours ? "text-[#D06A42]/70" : "text-ink-soft"}`}
                   style={{ top: i * SLOT_MIN * PX_PER_MIN }}>
                {fmt(s.time)}
              </div>
            ))}
          </div>
        </div>

        {/* Chair columns */}
        {chairsToRender.map((chair, chairIdx) => {
          const chairAppts = appts.filter((a) => a.chair_id === chair.id);
          const chairDoc = chair.doctor
            || (chair.doctor_id ? doctorMap[chair.doctor_id] : null);
          const colColor = doctorColorFor(chairDoc?.id || chair.id);
          const sched = chairDoc ? scheduleMap[chairDoc.id] : null;
          const onLeave = !!sched?.on_leave;
          return (
            <div key={chair.id} className="flex-1 min-w-[220px] border-l border-[#E8EAE9]" data-testid={`chair-col-${chair.id}`}>
              {/* Chair header with doctor identity */}
              <div
                className="h-16 border-b border-[#E8EAE9] px-2 flex items-center gap-2 relative overflow-hidden"
                style={{ background: `linear-gradient(180deg, ${colColor.bg} 0%, #ffffff 100%)` }}
                data-testid={`chair-header-${chair.id}`}
              >
                <DoctorBadge doctor={chairDoc} color={colColor} solo={isSolo} />
                <div className="min-w-0 flex-1">
                  <div className="font-display text-sm truncate leading-tight flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: chair.color || colColor.tint }} />
                    {isSolo ? "Your Chair" : chair.name}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest truncate mt-0.5"
                       style={{ color: colColor.fg }}>
                    {chairDoc ? `Dr. ${(chairDoc.name || "").replace(/^Dr\.?\s*/i, "")}` : "Unassigned"}
                  </div>
                </div>
                {onLeave && (
                  <span
                    className="absolute right-2 top-1.5 text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded-full bg-clay text-linen shadow-sm"
                    title={sched?.leave_note || "On leave"}
                    data-testid={`chair-leave-ribbon-${chair.id}`}
                  >
                    On leave
                  </span>
                )}
              </div>
              <div
                className="relative"
                style={{ height: colHeight }}
                ref={(el) => (gridRefs.current[chair.id] = el)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => onDropOnChair(e, chair)}
              >
                {/* On-leave overlay stripe */}
                {onLeave && (
                  <div
                    className="pointer-events-none absolute inset-0"
                    style={{
                      backgroundImage: "repeating-linear-gradient(45deg, rgba(208,106,66,0.08) 0 8px, transparent 8px 20px)",
                    }}
                  />
                )}
                {/* slot lines + click-to-create. Gap/closed slots get a
                    diagonal-striped background AND swallow the click so a
                    receptionist can never open the create dialog on them. */}
                {slots.map((s, i) => (
                  <div key={`${chair.id}-${s.time}`}
                       onClick={() => onCellClick(chair.id, s)}
                       data-testid={s.outOfHours ? `slot-gap-${chair.id}-${i}` : undefined}
                       className={`absolute inset-x-0 border-t border-dashed ${
                         s.outOfHours
                           ? "cursor-not-allowed border-[#D6D9D8]/70 bg-[repeating-linear-gradient(45deg,_rgba(93,104,99,0.10)_0_6px,_transparent_6px_14px)] hover:bg-[repeating-linear-gradient(45deg,_rgba(93,104,99,0.14)_0_6px,_transparent_6px_14px)]"
                           : "cursor-pointer border-[#EDEEEE] hover:bg-linen/40"
                       }`}
                       title={s.outOfHours ? "Outside working hours — update Settings → Working hours to open this slot" : undefined}
                       style={{ top: i * SLOT_MIN * PX_PER_MIN, height: SLOT_MIN * PX_PER_MIN }} />
                ))}

                {/* appointment cards — colour-coded per doctor */}
                {/* Google-Calendar imported blocks — read-only diagonal-striped
                    cards that visually reserve the time on the doctor's chair.
                    Rendered UNDERNEATH the appointment cards (chairAppts.map
                    below) so a real appointment that happens to overlap still
                    stays clickable. Filter: only busy blocks for THIS chair's
                    doctor, and only those that fall on the currently-viewed
                    calendar day (dayStart..dayStart+24h). */}
                {chairDoc && gcalBusy
                  .filter((g) => g.doctor_id === chairDoc.id)
                  .map((g) => {
                    const gs = new Date(g.start);
                    const ge = new Date(g.end);
                    if (ge <= dayStart || gs >= new Date(dayStart.getTime() + 24 * 3600 * 1000)) return null;
                    const top = ((Math.max(gs, dayStart) - dayStart) / 60000) * PX_PER_MIN;
                    const bot = ((Math.min(ge, new Date(dayStart.getTime() + 24 * 3600 * 1000)) - dayStart) / 60000) * PX_PER_MIN;
                    const height = Math.max(bot - top, 20);
                    return (
                      <div
                        key={`gcal-${g.id}`}
                        data-testid={`gcal-block-${g.id}`}
                        title={`Google Calendar · ${g.title}`}
                        className="absolute left-1 right-1 rounded-lg px-2 py-1 text-[11px] cursor-not-allowed select-none pointer-events-none"
                        style={{
                          top, height,
                          background: "repeating-linear-gradient(45deg, rgba(66,102,158,0.14) 0 8px, rgba(66,102,158,0.06) 8px 16px)",
                          border: "1px dashed rgba(66,102,158,0.6)",
                          color: "#2a4c7d",
                        }}
                      >
                        <div className="font-mono text-[10px] opacity-80 truncate">
                          {fmt(gs)} · Google
                        </div>
                        <div className="font-medium truncate">{g.title}</div>
                      </div>
                    );
                  })}
                {chairAppts.map((a) => {
                  const start = new Date(a.start);
                  const top = ((start - dayStart) / 60000) * PX_PER_MIN;
                  const height = Math.max(a.duration_min * PX_PER_MIN, 42);
                  const apptDoc = doctorMap[a.doctor_id] || chairDoc;
                  const col = doctorColorFor(apptDoc?.id || a.chair_id);
                  const s = statusStyle(a.status);
                  // Full-INTERVAL check — start-only would misflag a
                  // 12:30-13:00 appt as out-of-hours because 13:00 is
                  // itself in the break. Uses the shared helper so the
                  // banner and per-appt highlight always agree.
                  const isOutOfHours = _wh_isOOH(todayShifts, a);
                  return (
                    <div
                      key={a.id}
                      draggable
                      data-testid={`cal-appt-${a.id}`}
                      onDragStart={(e) => onDragStart(e, a)}
                      onClick={() => onEdit && onEdit(a)}
                      title={isOutOfHours
                        ? "This appointment falls outside the clinic's working hours."
                        : "Click to edit · drag to reschedule"}
                      className={`absolute left-1 right-1 rounded-lg px-2 py-1.5 text-xs border-l-4 shadow-sm cursor-pointer hover:shadow-md transition-shadow overflow-hidden ${s.strike ? "opacity-70" : ""} ${isOutOfHours ? "ring-1 ring-clay/50" : ""}`}
                      style={{ top, height,
                               background: col.bg,
                               color: col.fg,
                               borderLeftColor: isOutOfHours ? "#B94A48" : col.tint,
                             }}
                    >
                      <div className="flex items-center justify-between gap-1">
                        <div className="font-mono text-[10px] opacity-70 truncate">
                          {fmt(start)} · {a.duration_min}m
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {isOutOfHours && (
                            <span
                              data-testid={`appt-out-of-hours-${a.id}`}
                              className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded-full bg-clay/20 text-clay"
                              title="Outside working hours"
                            >
                              <AlertCircle className="w-2.5 h-2.5" /> Out of hrs
                            </span>
                          )}
                          <StatusPill status={a.status} />
                          <button onClick={(e) => { e.stopPropagation(); onSendReminder(a); }} className="opacity-60 hover:opacity-100" title="Send reminder">
                            <Send className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      <div className="mt-0.5 font-semibold truncate leading-tight" data-testid={`cal-appt-name-${a.id}`}>{pname(a.patient_id)}</div>
                      {a.clinic_patient_id && (
                        <div className="text-[9px] font-mono opacity-70 truncate leading-tight" data-testid={`cal-appt-cpid-${a.id}`}>
                          {a.clinic_patient_id}
                        </div>
                      )}
                      <div className="opacity-75 truncate leading-tight">{a.procedure}</div>
                      {height >= 60 && (
                        <div className="mt-0.5 text-[10px] opacity-60 truncate">
                          {!isSolo && (apptDoc?.name ? `Dr. ${(apptDoc.name || "").replace(/^Dr\.?\s*/i, "")}` : "Unassigned")}
                          {!isSolo && chair.name && <> · {chair.name}</>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---- Small helpers used by ChairGrid --------------------------------------
function DoctorBadge({ doctor, color, solo }) {
  if (!doctor) {
    return (
      <div className="w-10 h-10 rounded-full grid place-items-center text-ink-soft border border-dashed border-pine/30 shrink-0">
        <User className="w-5 h-5" />
      </div>
    );
  }
  const initials = (doctor.name || "?")
    .replace(/^Dr\.?\s*/i, "")
    .split(/\s+/).slice(0, 2).map((w) => w[0] || "").join("").toUpperCase();
  const size = solo ? "w-10 h-10 text-sm" : "w-10 h-10 text-sm";
  if (doctor.picture) {
    return (
      <img src={doctor.picture} alt={doctor.name}
           className={`${size} rounded-full object-cover shrink-0 border border-white shadow-sm`} />
    );
  }
  return (
    <div className={`${size} rounded-full grid place-items-center font-display shrink-0 shadow-sm`}
         style={{ background: color.tint, color: "#FFFFFF" }}>
      {initials || "Dr"}
    </div>
  );
}

const STATUS_LABEL = {
  scheduled:  { label: "Scheduled",  icon: Clock,        color: "#5D6863" },
  checked_in: { label: "Checked-in", icon: UserCheck,    color: "#8A3F1E" },
  in_chair:   { label: "In chair",   icon: Stethoscope,  color: "#334488" },
  completed:  { label: "Completed",  icon: CheckCircle2, color: "#2D6A4F" },
  cancelled:  { label: "Cancelled",  icon: XCircle,      color: "#B94A48" },
  no_show:    { label: "No-show",    icon: XCircle,      color: "#8A3F1E" },
};

function statusStyle(status) {
  return {
    strike: status === "cancelled" || status === "no_show",
  };
}

function StatusPill({ status }) {
  const meta = STATUS_LABEL[status] || STATUS_LABEL.scheduled;
  const Icon = meta.icon;
  return (
    <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded-full"
          style={{ background: `${meta.color}18`, color: meta.color }}>
      <Icon className="w-2.5 h-2.5" />
      {meta.label}
    </span>
  );
}

// ---- Chair ownership admin dialog ----------------------------------------
function ChairOwnershipDialog({ open, onClose, chairs, doctors, scheduleMap = {}, onEditSchedule, onChanged }) {
  const [saving, setSaving] = useState(null);
  const [adding, setAdding] = useState(false);
  // Plan cap comes from the centralized ClinicConfig — never hardcoded here.
  const cfg = useClinicConfig();
  const chairCap = cfg.caps?.chairs ?? 1;
  const canAdd = chairs.length < chairCap;

  const assign = async (chairId, doctorId) => {
    setSaving(chairId);
    try {
      await api.patch(`/chairs/${chairId}`, { doctor_id: doctorId || null });
      toast.success("Chair updated");
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e, "Failed to update"));
    } finally { setSaving(null); }
  };

  const addChair = async () => {
    setAdding(true);
    try {
      const nextIdx = chairs.length + 1;
      // Curated palette mirrors the backend onboarding seed so chair colours
      // stay visually consistent across newly added operatories.
      const palette = ["#1A362B", "#4A6B5D", "#D06A42", "#8CA88F", "#B6845F", "#3C5A47", "#7A5E4F", "#5C6C64"];
      await api.post("/chairs", {
        name: `Chair ${nextIdx}`,
        color: palette[(nextIdx - 1) % palette.length],
      });
      toast.success("Chair added");
      onChanged?.();
      cfg.invalidateAllTabs?.();
    } catch (e) {
      // Backend returns HTTP 402 {detail:{error:"cap_reached",...}} when the
      // tier cap is hit — surface the friendly message directly.
      const detail = e?.response?.data?.detail;
      if (detail?.error === "cap_reached") {
        toast.error(detail.message || `Your plan allows up to ${detail.limit} chairs.`);
      } else {
        toast.error(apiError(e, "Failed to add chair"));
      }
    } finally { setAdding(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="chair-ownership-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Users className="w-5 h-5" /> Chair ownership
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-ink-mid mb-2 flex items-center justify-between gap-2">
          <span>
            Every operatory in a multi-doctor clinic should be owned by a doctor.
            Booking flows will auto-suggest the owner as the treating doctor.
          </span>
          <span className="shrink-0 rounded-full bg-linen border border-[#E8EAE9] px-2 py-0.5 text-[10px] uppercase tracking-widest text-ink-mid"
                data-testid="chair-cap-badge">
            {chairs.length} / {chairCap}
          </span>
        </div>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {chairs.length === 0 && (
            <div className="text-sm text-ink-soft text-center py-6">No chairs yet.</div>
          )}
          {chairs.map((c) => {
            const doc = c.doctor || null;
            const sched = doc ? scheduleMap[doc.id] : null;
            return (
              <div key={c.id} className="rounded-xl border border-[#E8EAE9] px-3 py-2 flex items-center gap-3"
                   data-testid={`chair-row-${c.id}`}>
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: c.color || "#4A6B5D" }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-ink-soft mt-0.5 flex items-center gap-1.5">
                    {doc?.name ? `Dr. ${(doc.name || "").replace(/^Dr\.?\s*/i, "")}` : "Unassigned"}
                    {sched?.on_leave && <span className="text-clay">· On leave</span>}
                  </div>
                </div>
                <select
                  value={c.doctor_id || ""}
                  onChange={(e) => assign(c.id, e.target.value)}
                  disabled={saving === c.id}
                  data-testid={`chair-assign-${c.id}`}
                  className="rounded-lg border border-[#E8EAE9] px-3 py-1.5 text-xs bg-white min-w-[160px]"
                >
                  <option value="">— Unassigned —</option>
                  {doctors.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
                {doc && (
                  <button
                    onClick={() => onEditSchedule(doc.id)}
                    title="Edit this doctor's schedule and leaves"
                    data-testid={`chair-schedule-${c.id}`}
                    className="w-8 h-8 rounded-full grid place-items-center border border-[#E8EAE9] hover:bg-linen text-ink-mid"
                  >
                    <Clock className="w-3.5 h-3.5" />
                  </button>
                )}
                {saving === c.id && <Loader2 className="w-3.5 h-3.5 animate-spin text-ink-soft" />}
              </div>
            );
          })}
        </div>
        <div className="pt-3 mt-3 border-t border-[#E8EAE9] flex items-center justify-between gap-3">
          <div className="text-xs text-ink-mid">
            {canAdd
              ? `Your plan allows ${chairCap - chairs.length} more chair${chairCap - chairs.length === 1 ? "" : "s"}.`
              : `You've reached the ${chairCap}-chair limit on your current plan.`}
          </div>
          <button
            onClick={addChair}
            disabled={!canAdd || adding}
            data-testid="btn-add-chair"
            className="inline-flex items-center gap-1.5 rounded-full bg-pine text-linen px-4 py-2 text-sm hover:bg-pine/90 disabled:opacity-40 disabled:cursor-not-allowed"
            title={canAdd ? "Add a new operatory" : "Upgrade your plan to add more chairs"}
          >
            {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Add chair
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---- Doctor Schedule editor ---------------------------------------------
const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

function DoctorScheduleDialog({ doctorId, chairs, canEdit = false, onClose, onChanged }) {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newLeave, setNewLeave] = useState({ start_date: "", end_date: "", note: "" });

  useEffect(() => {
    if (!doctorId) { setSchedule(null); return; }
    setLoading(true);
    api.get(`/doctors/${doctorId}/schedule`)
      .then(({ data }) => setSchedule(data))
      .catch(() => toast.error("Could not load schedule"))
      .finally(() => setLoading(false));
  }, [doctorId]);

  if (!doctorId) return null;

  const updateDay = (d, field, value) => {
    setSchedule((s) => {
      const wh = { ...(s.working_hours || {}) };
      wh[d] = { ...(wh[d] || { open: "09:00", close: "18:00", closed: false }), [field]: value };
      return { ...s, working_hours: wh };
    });
  };

  const toggleDayClosed = (d) => {
    setSchedule((s) => {
      const wh = { ...(s.working_hours || {}) };
      const cur = wh[d] || { open: "09:00", close: "18:00", closed: false };
      wh[d] = { ...cur, closed: !cur.closed };
      return { ...s, working_hours: wh };
    });
  };

  const toggleDefaultChair = (cid) => {
    setSchedule((s) => {
      const list = new Set(s.default_chair_ids || []);
      if (list.has(cid)) list.delete(cid); else list.add(cid);
      return { ...s, default_chair_ids: Array.from(list) };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/doctors/${doctorId}/schedule`, {
        working_hours: schedule.working_hours || {},
        available: !!schedule.available,
        default_chair_ids: schedule.default_chair_ids || [],
      });
      toast.success("Schedule saved");
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    } finally { setSaving(false); }
  };

  const addLeave = async () => {
    if (!newLeave.start_date) return toast.error("Pick a start date");
    try {
      await api.post(`/doctors/${doctorId}/leaves`, newLeave);
      toast.success("Leave added");
      setNewLeave({ start_date: "", end_date: "", note: "" });
      const { data } = await api.get(`/doctors/${doctorId}/schedule`);
      setSchedule(data);
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e, "Failed to add leave"));
    }
  };

  const removeLeave = async (lid) => {
    try {
      await api.delete(`/leaves/${lid}`);
      const { data } = await api.get(`/doctors/${doctorId}/schedule`);
      setSchedule(data);
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e, "Failed"));
    }
  };

  const ownedChairs = chairs.filter((c) => c.doctor_id === doctorId);

  return (
    <Dialog open={!!doctorId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl p-0 max-h-[90vh] flex flex-col" data-testid="doctor-schedule-dialog">
        <DialogHeader className="p-5 pb-3 border-b border-[#E8EAE9]">
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Clock className="w-5 h-5" /> {schedule?.name || "Doctor"} — Schedule
          </DialogTitle>
          <div className="text-xs text-ink-mid">Working hours, availability toggle, default chairs, and leave calendar.</div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loading || !schedule ? (
            <div className="py-10 grid place-items-center text-ink-soft">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : (
            <>
              {/* Availability */}
              <div className="rounded-2xl border border-[#E8EAE9] p-4 flex items-center gap-3 justify-between">
                <div>
                  <div className="text-sm font-medium">Available for bookings</div>
                  <div className="text-xs text-ink-soft mt-0.5">Off means the doctor is treated as unavailable for new appointments.</div>
                </div>
                <button
                  data-testid="schedule-available-toggle"
                  disabled={!canEdit}
                  onClick={() => setSchedule({ ...schedule, available: !schedule.available })}
                  className={`w-12 h-7 rounded-full transition relative disabled:opacity-40 ${schedule.available ? "bg-pine" : "bg-pine/20"}`}
                >
                  <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all shadow ${schedule.available ? "left-6" : "left-1"}`} />
                </button>
              </div>

              {/* Working hours */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-2">Working hours (overrides clinic default)</div>
                <div className="rounded-2xl border border-[#E8EAE9] divide-y divide-[#E8EAE9]">
                  {WEEKDAYS.map((d) => {
                    const cfg = schedule.working_hours?.[d] || { open: "09:00", close: "18:00", closed: false };
                    return (
                      <div key={d} className="px-3 py-2 flex items-center gap-2" data-testid={`schedule-day-${d}`}>
                        <div className="w-24 text-sm capitalize">{d}</div>
                        <button
                          onClick={() => canEdit && toggleDayClosed(d)}
                          disabled={!canEdit}
                          data-testid={`schedule-day-toggle-${d}`}
                          className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${cfg.closed ? "bg-clay/20 text-clay" : "bg-pine/10 text-ink"} disabled:opacity-50`}
                        >
                          {cfg.closed ? "Closed" : "Open"}
                        </button>
                        {!cfg.closed && (
                          <>
                            <Time24Input value={cfg.open} onChange={(v) => updateDay(d, "open", v)}
                                   disabled={!canEdit}
                                   className="ml-auto rounded-lg border border-[#E8EAE9] px-2 py-1 text-xs w-[68px] text-center" />
                            <span className="text-ink-soft">→</span>
                            <Time24Input value={cfg.close} onChange={(v) => updateDay(d, "close", v)}
                                   disabled={!canEdit}
                                   className="rounded-lg border border-[#E8EAE9] px-2 py-1 text-xs w-[68px] text-center" />
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Default chairs */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-2">Default chairs · used when booking auto-suggests</div>
                {ownedChairs.length === 0 ? (
                  <div className="text-xs text-ink-soft rounded-xl border border-dashed border-[#E8EAE9] p-3 text-center">
                    No chairs assigned to this doctor yet. Use "Chair ownership" to assign chairs first.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {ownedChairs.map((c) => {
                      const on = (schedule.default_chair_ids || []).includes(c.id);
                      return (
                        <button
                          key={c.id}
                          disabled={!canEdit}
                          onClick={() => toggleDefaultChair(c.id)}
                          data-testid={`schedule-chair-${c.id}`}
                          className={`px-3 py-1.5 rounded-full border text-xs ${on ? "bg-pine text-linen border-pine" : "border-[#E8EAE9] text-ink bg-white"} disabled:opacity-50`}
                        >
                          {c.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Save button (working hours / chairs / available) */}
              {canEdit && (
                <div className="flex justify-end">
                  <button
                    onClick={save}
                    disabled={saving}
                    data-testid="schedule-save"
                    className="text-sm px-4 py-2 rounded-full bg-pine text-linen hover:bg-pine/90 inline-flex items-center gap-2 disabled:opacity-60">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    Save hours & preferences
                  </button>
                </div>
              )}

              {/* Leave calendar */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-2">Leaves</div>
                {canEdit && (
                  <div className="rounded-2xl border border-[#E8EAE9] p-3 mb-2 flex flex-wrap items-center gap-2">
                    <DateDMYInput value={newLeave.start_date}
                           data-testid="new-leave-start"
                           onChange={(e) => setNewLeave({ ...newLeave, start_date: e.target.value })}
                           className="rounded-lg border border-[#E8EAE9] px-2 py-1 text-xs" />
                    <span className="text-ink-soft text-xs">to</span>
                    <DateDMYInput value={newLeave.end_date}
                           data-testid="new-leave-end"
                           onChange={(e) => setNewLeave({ ...newLeave, end_date: e.target.value })}
                           placeholder="optional"
                           className="rounded-lg border border-[#E8EAE9] px-2 py-1 text-xs" />
                    <input type="text" value={newLeave.note}
                           data-testid="new-leave-note"
                           placeholder="Reason (optional)"
                           onChange={(e) => setNewLeave({ ...newLeave, note: e.target.value })}
                           className="flex-1 min-w-[140px] rounded-lg border border-[#E8EAE9] px-2 py-1 text-xs" />
                    <button
                      onClick={addLeave}
                      data-testid="add-leave-btn"
                      className="text-xs px-3 py-1.5 rounded-full bg-clay text-linen hover:brightness-110">
                      Add leave
                    </button>
                  </div>
                )}
                {(schedule.leaves || []).length === 0 ? (
                  <div className="text-xs text-ink-soft text-center py-3">No leaves recorded.</div>
                ) : (
                  <ul className="rounded-2xl border border-[#E8EAE9] divide-y divide-[#E8EAE9]">
                    {schedule.leaves.map((l) => (
                      <li key={l.id} className="px-3 py-2 flex items-center gap-2" data-testid={`leave-row-${l.id}`}>
                        <div className="w-2 h-2 rounded-full bg-clay shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-ink truncate">
                            {l.start_date === l.end_date ? l.start_date : `${l.start_date} → ${l.end_date}`}
                          </div>
                          {l.note && <div className="text-[11px] text-ink-soft truncate">{l.note}</div>}
                        </div>
                        {canEdit && (
                          <button
                            onClick={() => removeLeave(l.id)}
                            data-testid={`remove-leave-${l.id}`}
                            className="w-7 h-7 rounded-full text-ink-soft hover:text-clay hover:bg-linen grid place-items-center">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


// -----------------------------------------------------------------------------
// OptimizeDialog: shows the AI-proposed reshuffle for the current day.
// -----------------------------------------------------------------------------
function OptimizeDialog({ open, onClose, loading, applying, plan, day, onApply }) {
  const dateLabel = day.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  const hasMoves = plan && plan.moves && plan.moves.length > 0;

  const fmtDelta = (n, suffix = "") => {
    const s = n > 0 ? `+${n}` : `${n}`;
    return `${s}${suffix}`;
  };
  const fmtMinutes = (m) => {
    if (!m || m < 0) return "0m";
    const h = Math.floor(m / 60); const mm = m % 60;
    return (h && mm) ? `${h}h ${mm}m` : (h ? `${h}h` : `${mm}m`);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-white p-0 max-h-[90vh] overflow-hidden flex flex-col" data-testid="ai-optimize-dialog">
        <DialogHeader className="p-6 pb-4 border-b border-[#E8EAE9]">
          <DialogTitle className="font-display text-2xl text-ink flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-clay" /> AI-optimised day
          </DialogTitle>
          <div className="text-xs text-ink-mid mt-0.5">{dateLabel}</div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {loading && (
            <div className="py-16 flex flex-col items-center gap-3 text-ink-mid" data-testid="optimize-loading">
              <Loader2 className="w-6 h-6 animate-spin text-clay" />
              <div className="text-sm">Analysing chairs, gaps and constraints…</div>
            </div>
          )}

          {!loading && plan && (
            <>
              {/* Metrics summary */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="optimize-metrics">
                <MetricCard
                  label="Idle chair time"
                  before={fmtMinutes(plan.metrics.before.total_gap_min)}
                  after={fmtMinutes(plan.metrics.after.total_gap_min)}
                  delta={-plan.metrics.delta.gap_min}
                  deltaLabel={`${fmtDelta(-plan.metrics.delta.gap_min, "m")} saved`}
                  good={plan.metrics.delta.gap_min > 0}
                />
                <MetricCard
                  label="Utilisation"
                  before={`${plan.metrics.before.utilization}%`}
                  after={`${plan.metrics.after.utilization}%`}
                  delta={plan.metrics.delta.utilization}
                  deltaLabel={fmtDelta(plan.metrics.delta.utilization, "pp")}
                  good={plan.metrics.delta.utilization > 0}
                />
                <MetricCard
                  label="Moves proposed"
                  before={`${plan.movable_count} movable`}
                  after={`${plan.moves.length} moves`}
                  delta={plan.locked_count}
                  deltaLabel={`${plan.locked_count} locked`}
                  good={true}
                />
              </div>

              {/* Narrative */}
              {plan.narrative && (
                <div className="rounded-2xl bg-linen/60 border border-[#E8EAE9] p-4 flex gap-3" data-testid="optimize-narrative">
                  <div className="w-8 h-8 rounded-full bg-clay/15 text-clay grid place-items-center shrink-0">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <div className="text-sm text-ink/80 leading-relaxed">{plan.narrative}</div>
                </div>
              )}

              {/* Moves list */}
              <div>
                <div className="overline text-ink-soft mb-2">Proposed moves</div>
                {!hasMoves ? (
                  <div className="rounded-2xl border border-dashed border-[#E8EAE9] py-8 text-center text-ink-soft text-sm" data-testid="optimize-empty">
                    Your day is already tightly packed — no improvements found.
                  </div>
                ) : (
                  <ul className="rounded-2xl border border-[#E8EAE9] divide-y divide-[#E8EAE9] overflow-hidden">
                    {plan.moves.map((m) => (
                      <li key={m.appt_id}
                          className="px-4 py-3 flex items-center gap-3 hover:bg-linen/40"
                          data-testid={`optimize-move-${m.appt_id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-ink truncate">{m.patient_name}</div>
                          <div className="text-[11px] text-ink-soft truncate">{m.procedure} · {m.duration_min}m</div>
                        </div>
                        <div className="flex items-center gap-2 text-xs shrink-0">
                          <span className="font-mono text-ink-mid">{m.from.chair_name || "—"} @ {m.from.start_hm}</span>
                          <ArrowRight className="w-3.5 h-3.5 text-clay" />
                          <span className="font-mono text-ink font-semibold">{m.to.chair_name || "—"} @ {m.to.start_hm}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {plan.window && (
                <div className="text-[11px] text-ink-soft flex items-center gap-1.5">
                  <Info className="w-3 h-3" /> Optimised within working hours {plan.window.open_hm}–{plan.window.close_hm}. Completed, cancelled, no-show and past appointments were locked.
                </div>
              )}
            </>
          )}
        </div>

        <div className="p-4 border-t border-[#E8EAE9] flex items-center justify-end gap-2 shrink-0 bg-white">
          <button
            onClick={onClose}
            data-testid="optimize-cancel"
            className="text-sm px-4 py-2 rounded-full text-ink-mid hover:bg-linen">
            {hasMoves ? "Discard" : "Close"}
          </button>
          {hasMoves && (
            <button
              onClick={onApply}
              disabled={applying}
              data-testid="optimize-apply"
              className="text-sm px-4 py-2 rounded-full bg-pine text-linen hover:bg-pine/90 inline-flex items-center gap-2 disabled:opacity-60">
              {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Apply {plan.moves.length} move{plan.moves.length === 1 ? "" : "s"}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MetricCard({ label, before, after, delta, deltaLabel, good }) {
  return (
    <div className="rounded-2xl border border-[#E8EAE9] bg-white p-3">
      <div className="text-[10px] uppercase tracking-widest text-ink-soft">{label}</div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="text-xs text-ink-soft line-through">{before}</span>
        <ArrowRight className="w-3 h-3 text-ink-disabled" />
        <span className="font-display text-xl text-ink leading-none">{after}</span>
      </div>
      <div className={`mt-1 text-[11px] ${good ? "text-[#2D6A4F]" : "text-ink-mid"}`}>
        {deltaLabel}
      </div>
    </div>
  );
}
